import Alert from 'react-s-alert';
import { observable, action, computed, runInAction } from 'mobx';

import { AppManager, Input, routerStore } from 'core';

import { LockPathMutation, LockedFilesQuery } from 'apps/repository/gql';

import { Status, StatusType } from 'libs/gutClient/types';
import { analytics } from 'core/analytics';
import { clientStore } from 'apps/client/app';
import type { SyncRepoAppManager } from './appManager';

export class LockingManager extends AppManager {
  static registryName = 'LockingManager';

  syncRepoAppManager: SyncRepoAppManager = null;

  @observable lockedFilesMap: Record<string, string> = {};

  constructor(parent: SyncRepoAppManager, init = {}, options = {}) {
    super(parent, init, options);
    this.syncRepoAppManager = parent;
  }

  @computed get lockedPaths() {
    return Object.keys(this.lockedFilesMap);
  }

  @computed get myLockedPaths() {
    // @ts-ignore
    const username = clientStore.signedInProfile?.user?.username;
    return Object.entries(this.lockedFilesMap)
      .filter(([, value]) => value === username)
      .map(([key]) => key);
  }

  @computed get remoteLockedPathsMap() {
    const username = clientStore.signedInProfile?.user?.username;
    const remoteLockedPathsMap: typeof this.lockedFilesMap = {};
    Object.entries(this.lockedFilesMap).forEach(([key, value]) => {
      if (value !== username) remoteLockedPathsMap[key] = value;
    });
    return remoteLockedPathsMap;
  }

  @computed get remoteLockedPaths() {
    return Object.keys(this.remoteLockedPathsMap);
  }

  @computed get localLocksNoChanges() {
    const noChanges = this.syncRepoAppManager.localStatus.filter(
      status => status.type === StatusType.NoChange,
    );
    const noChangesMap: Record<string, Status> = {};
    noChanges.forEach(change => {
      noChangesMap[change.path] = change;
    });
    return this.myLockedPaths.map(path => noChangesMap[path]).filter(Boolean);
  }

  @action QueryLockedFiles = async () => {
    const lockedFilesQuery = new LockedFilesQuery(this, {
      variables: {
        space:
          routerStore.route.params.profile ||
          routerStore.route.params.initiative,
        slug: routerStore.route.params.slug,
        path: '/',
      },
      fetchPolicy: 'network-only',
      onSysErr: analytics.logError,
      onUserErr: analytics.logError,
    });
    lockedFilesQuery.process((result: any) => {
      if (result) {
        runInAction(() => {
          this.lockedFilesMap = {};
          result.forEach((lockInfo: any) => {
            this.lockedFilesMap[lockInfo.path] = lockInfo.lockedBy.username;
          });
        });
        lockedFilesQuery.disposer();
      }
    });
    lockedFilesQuery.query();
  };

  @action LockLocalDriveSelection = async () => {
    const success = await this.lockPaths(
      this.syncRepoAppManager.localTableSelectedRowIds,
      true,
    );
    if (success) this.syncRepoAppManager.localTableSelectedRowIds = [];
  };

  @action UnlockLocalDriveSelection = async () => {
    const success = await this.lockPaths(
      this.syncRepoAppManager.localTableSelectedRowIds,
      false,
    );
    if (success) this.syncRepoAppManager.localTableSelectedRowIds = [];
  };

  @action LockLocalChangesSelection = async () => {
    const success = await this.lockPaths(
      this.syncRepoAppManager.selectedLocalChangesTableRowPaths,
      true,
    );
    if (success) this.syncRepoAppManager.selectedLocalChangesTableRowPaths = [];
  };

  @action UnlockLocalChangesSelection = async () => {
    const success = await this.lockPaths(
      this.syncRepoAppManager.selectedLocalChangesTableRowPaths,
      false,
    );
    if (success) this.syncRepoAppManager.selectedLocalChangesTableRowPaths = [];
  };

  @action lockPaths = (paths: string[], lock: boolean) => {
    const input = new Input({
      lockInput: {
        projectId: this.syncRepoAppManager.repoId,
        paths,
        lock,
      },
    });

    return new Promise(resolve => {
      const lockPathSuccess = (result: any) => {
        if (result) {
          runInAction(() => {
            result.forEach((lockInfo: any) => {
              if (lockInfo.locked === 'success_locked') {
                this.lockedFilesMap[lockInfo.path] = lockInfo.lockedBy.username;
              }
              if (lockInfo.locked === 'success_unlocked') {
                delete this.lockedFilesMap[lockInfo.path];
              }
            });
            resolve(true);
            this.QueryLockedFiles();
          });
        }
      };

      const handleError = (err: Error) => {
        analytics.logError(err);
        const msgAction = lock ? 'locking' : 'unlocking';
        Alert.error(
          `There was an error during ${msgAction}. This has been reported.`,
        );
        resolve(false);
      };

      const options = {
        onSuccess: (mutationResult: [Object]) =>
          lockPathSuccess(mutationResult),
        onUserErr: handleError,
        onSysErr: handleError,
      };
      const lockPathMutation = new LockPathMutation(input, null, options, true);
      lockPathMutation.mutate();
    });
  };
}
